import {BusinessFeeNegotiationParty} from '@afriex/core/domain/enums';
import type {
  BusinessCorridorInterest,
  BusinessFeeNegotiation,
  BusinessFeeNegotiationRound,
} from '@afriex/core/domain/types';
import {
  BUSINESS_FEE_NEGOTIATION_SALES_CALL_NOTE,
  BusinessFeeNegotiationFilter,
  getNegotiationDisplay,
} from '@afriex/core/domain/types';
import {formatFeeAmount, getFeeScheduleChannelLabel} from '@afriex/core/utils';

import * as formatDate from '@/utils/date';

const EMPTY = '-';

/** In the order an admin works them. */
export const NEGOTIATION_TABS = [
  BusinessFeeNegotiationFilter.AwaitingAdmin,
  BusinessFeeNegotiationFilter.AwaitingBusiness,
  BusinessFeeNegotiationFilter.Completed,
];

/** Queue tab labels, in Afriex's voice. */
export const NEGOTIATION_FILTER_LABELS: Record<
  BusinessFeeNegotiationFilter,
  string
> = {
  [BusinessFeeNegotiationFilter.Ongoing]: 'Ongoing',
  [BusinessFeeNegotiationFilter.Completed]: 'Completed',
  [BusinessFeeNegotiationFilter.AwaitingAdmin]: 'Awaiting AFX',
  [BusinessFeeNegotiationFilter.AwaitingBusiness]: 'Awaiting merchant',
};

/** Whether the merchant withdrew to speak to Sales. */
export const isSalesCallWithdrawal = (
  negotiation: BusinessFeeNegotiation,
): boolean =>
  negotiation.withdrawalNote === BUSINESS_FEE_NEGOTIATION_SALES_CALL_NOTE;

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** A monthly volume as whole dollars, "$25,000", or a dash when missing. */
export const formatMonthlyVolume = (value?: number): string =>
  typeof value === 'number' ? usd.format(value) : EMPTY;

/** "28 Sep 2026", or a dash when there is no date. */
export const formatDay = (value?: string | Date): string =>
  value ? formatDate.DDMonYYYY(String(value)) : EMPTY;

/** "28 Sep 2026, 10:42 AM", or a dash when there is no date. */
export const formatDayTime = (value?: string | Date): string =>
  value ? formatDate.DDMonYYYY(String(value), true) : EMPTY;

const lastRound = (
  negotiation: BusinessFeeNegotiation,
): BusinessFeeNegotiationRound | undefined =>
  negotiation.rounds?.[negotiation.rounds.length - 1];

/** When the negotiation last moved: its newest round, else its last update. */
export const getLastActivity = (negotiation: BusinessFeeNegotiation): string =>
  formatDay(
    lastRound(negotiation)?.at ??
      negotiation.updatedAt ??
      negotiation.createdAt,
  );

/** When a settled negotiation ended. */
export const getClosedOn = (negotiation: BusinessFeeNegotiation): string =>
  formatDay(
    negotiation.acceptedAt ??
      negotiation.rejectedAt ??
      negotiation.updatedAt ??
      negotiation.createdAt,
  );

/** How many corridors the standing offer covers. */
export const getCorridorCount = (negotiation: BusinessFeeNegotiation): number =>
  lastRound(negotiation)?.lines.length ?? 0;

/** The tab a negotiation sits under. */
export const getNegotiationTab = (
  negotiation: BusinessFeeNegotiation,
): BusinessFeeNegotiationFilter => {
  const display = getNegotiationDisplay(negotiation);
  if (!display.isOpen) {
    return BusinessFeeNegotiationFilter.Completed;
  }
  return display.waitingOn === BusinessFeeNegotiationParty.Afriex
    ? BusinessFeeNegotiationFilter.AwaitingAdmin
    : BusinessFeeNegotiationFilter.AwaitingBusiness;
};

/** The status as one badge label: whose move it is, or how it ended. */
export const getStatusLabel = (negotiation: BusinessFeeNegotiation): string => {
  const display = getNegotiationDisplay(negotiation);
  if (!display.isOpen) {
    if (isSalesCallWithdrawal(negotiation)) {
      return 'Withdrawn (sales call)';
    }
    return display.outcomeShortLabel ?? 'Closed';
  }
  return NEGOTIATION_FILTER_LABELS[getNegotiationTab(negotiation)];
};

/** The round label the review shows: "Round 2 of 3", or where it was agreed. */
export const getRoundLabel = (negotiation: BusinessFeeNegotiation): string => {
  const {roundLabel} = getNegotiationDisplay(negotiation);
  return negotiation.acceptedRoundIndex === undefined
    ? roundLabel
    : `Agreed in round ${negotiation.acceptedRoundIndex + 1}`;
};

/** A corridor in one line: "KES · Mobile money". */
export const formatCorridorLabel = (
  corridor: BusinessCorridorInterest,
): string =>
  `${corridor.currencyCode} · ${getFeeScheduleChannelLabel(corridor.channel)}`;

/** Who sent a round: "Ada Obi (merchant)" or "Tunde Ade (AFX)". */
export const formatRoundAuthor = (round: BusinessFeeNegotiationRound): string =>
  `${round.byName ?? 'Unknown'} (${
    round.by === BusinessFeeNegotiationParty.Afriex ? 'AFX' : 'merchant'
  })`;

/** A round's prices in one line: "KES 0.40%, NGN 0.80%". */
export const formatRoundOffer = (round: BusinessFeeNegotiationRound): string =>
  round.lines
    .map((line) => {
      const price = formatFeeAmount(line.proposedAmount);
      // A flat price already names its currency; a percentage needs the corridor's.
      return line.proposedAmount.currencyCode
        ? price
        : `${line.corridor.currencyCode} ${price}`;
    })
    .join(', ');
